import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Search, ArrowDown, CheckCircle2 } from "lucide-react";
import { lineReveal } from "@/lib/motion";
import { LiveCounter } from "@/components/LiveCounter";

const HERO_BG =
  "https://images.unsplash.com/photo-1576602976047-174e57a47881?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwaGFybWFjeSUyMHNoZWxmJTIwY2xpbmljYWx8ZW58MHx8fHwxNzg1NTI5NDc3fDA&ixlib=rb-4.1.0&q=85";
const SKYLINE =
  "https://images.unsplash.com/photo-1625726411847-8cbb60cc71e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwY2l0eSUyMHNreWxpbmV8ZW58MHx8fHwxNzg1NTI5NDg5fDA&ixlib=rb-4.1.0&q=85";

const HEADLINE_LINES = ["Stop driving", "pharmacy to pharmacy."];

export const Hero = ({ onJoin, count }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const skylineY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-screen overflow-hidden bg-white"
      data-testid="hero-section"
    >
      {/* Background pharmacy shelf */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_BG}
          alt="Modern pharmacy shelf"
          className="h-[115%] w-full object-cover"
          style={{ filter: "blur(3px)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/78 to-white" />
        <div className="absolute inset-0 bg-white/40" />
      </motion.div>

      {/* Parallax skyline strip */}
      <motion.div
        style={{ y: skylineY }}
        className="pointer-events-none absolute bottom-0 inset-x-0 -z-10 h-56 opacity-40"
      >
        <img
          src={SKYLINE}
          alt="Bhubaneswar skyline"
          className="h-full w-full object-cover [mask-image:linear-gradient(to_top,black,transparent)]"
        />
      </motion.div>

      <div className="noise-overlay" />

      {/* Floating location pin */}
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.9, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="absolute right-[8%] top-[26%] hidden lg:block"
        data-testid="hero-location-pin"
      >
        <motion.div
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center"
        >
          <div
            className="grid h-14 w-14 place-items-center rounded-2xl text-white shadow-[0_16px_40px_-10px_rgba(234,88,12,0.6)]"
            style={{ backgroundColor: "var(--urgent-orange)" }}
          >
            <MapPin size={26} strokeWidth={2.4} />
          </div>
          <span className="mt-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-slate-700 backdrop-blur">
            Bhubaneswar
          </span>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto flex min-h-screen max-w-6xl flex-col justify-center px-5 pt-28 pb-24 sm:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mb-7 inline-flex w-fit items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 backdrop-blur"
          data-testid="hero-eyebrow"
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: "var(--action-green)" }}
          />
          India&apos;s Medicine Search Engine
        </motion.div>

        <h1
          className="font-display max-w-4xl text-5xl font-extrabold leading-[1.02] tracking-tight text-slate-900 sm:text-6xl lg:text-7xl"
          data-testid="hero-headline"
        >
          {HEADLINE_LINES.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                custom={i}
                variants={lineReveal}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {i === 1 ? (
                  <span>
                    pharmacy to{" "}
                    <span
                      className="relative inline-block"
                      style={{ color: "var(--action-green)" }}
                    >
                      pharmacy.
                      <svg
                        className="absolute -bottom-2 left-0 w-full"
                        height="10"
                        viewBox="0 0 200 10"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M2 7 Q 50 2 100 6 T 198 5"
                          stroke="var(--action-green)"
                          strokeWidth="3"
                          fill="none"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                  </span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-7 max-w-xl text-lg text-slate-600 sm:text-xl"
          data-testid="hero-subheadline"
        >
          Bhubaneswar&apos;s Medicine Search Engine is{" "}
          <span className="font-semibold text-slate-900">Coming Soon.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center"
        >
          <button
            onClick={onJoin}
            data-testid="hero-waitlist-button"
            className="group inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white transition-[transform,box-shadow] duration-300 hover:scale-[1.04] glow-green"
            style={{ backgroundColor: "var(--action-green)" }}
          >
            Join the VIP Waitlist
            <ArrowDown
              size={18}
              className="transition-transform duration-300 group-hover:translate-y-1"
            />
          </button>
          <span className="text-sm text-slate-500">
            Free delivery on your first order.
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.05, duration: 0.6 }}
          className="mt-6"
        >
          <LiveCounter count={count} testId="hero-live-counter" />
        </motion.div>

        {/* Product mock search card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 w-full max-w-md rounded-2xl border border-slate-100 bg-white/80 p-4 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)] backdrop-blur-xl"
          data-testid="hero-search-mock"
        >
          <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
            <Search size={18} className="text-slate-400" />
            <span className="text-sm text-slate-500">Search “Azithromycin 500mg”…</span>
          </div>
          <div className="mt-3 flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">Apollo Chemist · 1.2 km</p>
              <p className="text-xs text-slate-500">Saheed Nagar, Bhubaneswar</p>
            </div>
            <span
              className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-white"
              style={{ backgroundColor: "var(--action-green)" }}
            >
              <CheckCircle2 size={13} /> In stock
            </span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
